import React, { useState, Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    isEmpty,
    isPhoneError,
    isPostCodeError,
} from "../../../store/services/helpers/validation";
import { commonService } from "../../../store/services/ServiceCommon";
import { LOADING_TIME } from "../../../store/config";
import { setSelectCountriesOptions } from "../../../store/services/helpers/functions";
import { userProfileService } from "../../../store/services/ServiceUserProfile";
import { addressService } from "../../../store/services/ServiceAddress";
import { addUser } from "../../../store/actions";
import ModalResponse from "../../../components/modals/ModalResponse";
import { Button, Form, Grid, Header, Modal } from "semantic-ui-react";

function displayValue(value) {
    const text = (value || "").trim();
    return text || "—";
}

function ProfileAddress() {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalIsError, setModalIsError] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [formError, setFormError] = useState("");
    const [addressId, setAddressId] = useState(0);
    const [countryList, setCountryList] = useState([]);
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [street, setStreet] = useState("");
    const [postCode, setPostCode] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [hasGoogle, setHasGoogle] = useState(false);
    const [hasPassword, setHasPassword] = useState(true);
    const [draft, setDraft] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        street: "",
        postCode: "",
        city: "",
        country: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const result = await commonService.getChoices();
            if (result.status === 200) {
                setCountryList(result.response.countries);
            }

            const dataProfile = await userProfileService.getUserProfile();
            const profile = dataProfile && dataProfile[0];
            if (profile) {
                setEmail(profile.email || (profile.user && profile.user.email) || "");
                setFirstName(profile.firstname || "");
                setLastName(profile.lastname || "");
                setPhone(profile.phone || "");
                setHasGoogle(!!profile.has_google);
                setHasPassword(!!profile.has_password);
                if (profile.address) {
                    setAddressId(profile.address.id);
                    setCountry(profile.address.country || "");
                    setCity(profile.address.city || "");
                    setStreet(profile.address.street || "");
                    setPostCode(profile.address.postcode || "");
                }
            }

            setTimeout(function () {
                setIsLoading(false);
            }, LOADING_TIME);
        };

        fetchData();
    }, []);

    const countryOptions = setSelectCountriesOptions(countryList);
    const countryName = (countryList && countryList[country]) || country;
    const addressLine = [street, [postCode, city].filter(Boolean).join(" "), countryName]
        .map((part) => (part || "").trim())
        .filter(Boolean)
        .join(", ");

    const openEdit = () => {
        setFormError("");
        setDraft({
            firstName,
            lastName,
            phone,
            street,
            postCode,
            city,
            country,
        });
        setEditOpen(true);
    };

    const setDraftField = (field, value) => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const handleSubmit = async () => {
        setIsProcessing(true);
        setFormError("");

        const addressStarted = !!(draft.street || draft.city || draft.postCode || draft.country);
        if (!draft.firstName || !draft.lastName) {
            setFormError("Vyplň meno a priezvisko.");
            setIsProcessing(false);
            return;
        }
        if (draft.phone && isPhoneError(draft.phone)) {
            setFormError("Telefón zadaj vo formáte +421901234567.");
            setIsProcessing(false);
            return;
        }
        if (addressStarted && (isPostCodeError(draft.postCode) || !draft.street || !draft.city || !draft.country)) {
            setFormError("Adresu vyplň celú: ulica, mesto, PSČ a krajina.");
            setIsProcessing(false);
            return;
        }

        let savedAddressId = addressId;
        if (addressStarted) {
            const dataAddress = {
                address_line: "",
                street: draft.street,
                city: draft.city,
                postcode: draft.postCode,
                country: draft.country,
            };
            if (addressId !== 0) {
                const resultAddress = await addressService.updateBaseAddress(addressId, dataAddress);
                if (!resultAddress || resultAddress.status !== 200) {
                    setFormError("Adresu sa nepodarilo uložiť.");
                    setIsProcessing(false);
                    return;
                }
            } else {
                const resultAddress = await addressService.createBaseAddress(dataAddress);
                if (!resultAddress || resultAddress.status !== 201) {
                    setFormError("Adresu sa nepodarilo uložiť.");
                    setIsProcessing(false);
                    return;
                }
                savedAddressId = resultAddress.response.id;
                setAddressId(savedAddressId);
            }
        }

        const saved = await userProfileService.updateUserProfile({
            firstname: draft.firstName,
            lastname: draft.lastName,
            phone: draft.phone,
            address: savedAddressId || undefined,
        });

        if (!saved) {
            setFormError("Profil sa nepodarilo uložiť.");
            setIsProcessing(false);
            return;
        }

        setFirstName(draft.firstName);
        setLastName(draft.lastName);
        setPhone(draft.phone);
        setStreet(draft.street);
        setCity(draft.city);
        setPostCode(draft.postCode);
        setCountry(draft.country);
        dispatch(addUser({
            ...currentUser,
            firstname: draft.firstName,
            lastname: draft.lastName,
        }));
        setIsProcessing(false);
        setEditOpen(false);
        setModalMessage("Profil bol uložený.");
        setModalIsError(false);
        setModalOpen(true);
    };

    const signInMethod = hasGoogle && hasPassword
        ? "Google; Heslo"
        : hasGoogle
            ? "Google"
            : hasPassword
                ? "Heslo"
                : "—";

    const rowStyle = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "2rem",
        padding: "1rem 0",
        borderBottom: "1px solid #eeeeee",
    };

    return (
        <Fragment>
            <Grid style={{ opacity: isLoading ? 0.6 : 1 }}>
                <Grid.Column computer={8} tablet={16} mobile={16}>
                    <Header as="h3">OSOBNÉ ÚDAJE</Header>
                    <div style={rowStyle}>
                        <span>Prihlasovací e-mail</span>
                        <strong>{displayValue(email)}</strong>
                    </div>
                    <div style={rowStyle}>
                        <span>Spôsob prihlásenia</span>
                        <strong>{signInMethod}</strong>
                    </div>
                    <div style={rowStyle}>
                        <span>Meno</span>
                        <strong>{displayValue([firstName, lastName].join(" "))}</strong>
                    </div>
                    <div style={rowStyle}>
                        <span>Telefón</span>
                        <strong>{displayValue(phone)}</strong>
                    </div>
                    <div style={rowStyle}>
                        <span>Adresa</span>
                        <strong style={{ textAlign: "right" }}>{displayValue(addressLine)}</strong>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "1.25rem", marginTop: "1.25rem" }}>
                        <Button
                            type="button"
                            onClick={openEdit}
                            style={{
                                background: "var(--primary)",
                                color: "white",
                                margin: 0,
                            }}
                        >
                            Upraviť
                        </Button>
                        {hasGoogle && !hasPassword ? (
                            <button
                                type="button"
                                onClick={() => {
                                    const url = new URL(window.location.href);
                                    url.searchParams.set("tab", "password");
                                    window.history.pushState({}, "", url.pathname + url.search);
                                    window.dispatchEvent(new Event("profile-tab"));
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    color: "var(--primary)",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                            >
                                Nastaviť bezpečnostné heslo
                            </button>
                        ) : null}
                        {hasGoogle && hasPassword ? (
                            <button
                                type="button"
                                disabled={isProcessing}
                                onClick={async () => {
                                    setIsProcessing(true);
                                    const removed = await userProfileService.unlinkGoogle();
                                    setIsProcessing(false);
                                    if (!removed) {
                                        setModalMessage("Google sa nepodarilo odpojiť.");
                                        setModalIsError(true);
                                        setModalOpen(true);
                                        return;
                                    }
                                    setHasGoogle(false);
                                    setModalMessage("Google bol odpojený. Prihlásenie ostáva heslom.");
                                    setModalIsError(false);
                                    setModalOpen(true);
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    color: "var(--primary)",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                            >
                                Odpojiť Google
                            </button>
                        ) : null}
                    </div>
                </Grid.Column>
            </Grid>

            <Modal open={editOpen} onClose={() => !isProcessing && setEditOpen(false)} size="small">
                <Modal.Header>Upraviť profil</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Input
                                required
                                label="Meno"
                                value={draft.firstName}
                                onChange={(e) => setDraftField("firstName", e.target.value)}
                            />
                            <Form.Input
                                required
                                label="Priezvisko"
                                value={draft.lastName}
                                onChange={(e) => setDraftField("lastName", e.target.value)}
                            />
                        </Form.Group>
                        <Form.Input
                            label="Telefón"
                            placeholder="+421901234567"
                            value={draft.phone}
                            onChange={(e) => setDraftField("phone", e.target.value)}
                            error={!isEmpty(draft.phone) ? isPhoneError(draft.phone) : false}
                        />
                        <Form.Input
                            label="Ulica a popisné číslo"
                            value={draft.street}
                            onChange={(e) => setDraftField("street", e.target.value)}
                        />
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Mesto/Obec"
                                value={draft.city}
                                onChange={(e) => setDraftField("city", e.target.value)}
                            />
                            <Form.Input
                                label="PSČ"
                                value={draft.postCode}
                                onChange={(e) => setDraftField("postCode", e.target.value)}
                                error={!isEmpty(draft.postCode) ? isPostCodeError(draft.postCode) : false}
                            />
                        </Form.Group>
                        <Form.Select
                            search
                            label="Krajina"
                            placeholder="Krajina"
                            options={countryOptions}
                            value={draft.country}
                            onChange={(e, data) => setDraftField("country", data.value)}
                        />
                        {formError ? <p style={{ color: "#9f3a38" }}>{formError}</p> : null}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button type="button" disabled={isProcessing} onClick={() => setEditOpen(false)}>
                        Zrušiť
                    </Button>
                    <Button
                        type="button"
                        loading={isProcessing}
                        disabled={isProcessing}
                        onClick={handleSubmit}
                        style={{ background: "var(--primary)", color: "white" }}
                    >
                        Potvrdiť
                    </Button>
                </Modal.Actions>
            </Modal>

            <ModalResponse
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                icon={modalIsError ? "warning circle" : "checkmark"}
                header={modalIsError ? "Aktualizácia neúspešná" : "Aktualizácia úspešná"}
                message={modalMessage}
                onClick={() => setModalOpen(false)}
            />
        </Fragment>
    );
}

export default ProfileAddress;
